import type { AvailableUnit, UnitDetailsData } from '@/core/api/models/properties';
import type { Property } from '@/core/api/models/profile';
import type { UnitInstallmentsMap } from '@/core/api/models/unitInstallments';
import { logger } from '@/core/logger';
import { clearDynatraceUser } from '@/core/services/dynatraceService';
import { secureStorage } from '@/core/storage';
import { clearStaleKeychainOnFreshInstall } from '@/core/storage/freshInstall';
import { encryptedStorage } from '@/core/wrappers/encryptedStorage';
import { AppState as RNAppState, Platform } from 'react-native';
import { create } from 'zustand';

/** Loan / ADHA application fields derived from login unit-details response */
export interface AdhaLoanDetails {
    adhaApplicationNumber?: string;
    sanctionedLoanAmount?: number;
    projectId?: string;
    projectName?: string;
    projectLocation?: string;
    createdDate?: string;
}

/** Active ADHA web reservation — booking API fields + sticky listing-row snapshot for details. */
export interface PendingUnitReservation {
    unitId: string;
    unitTitle: string;
    requestedOn: string;
    unit: AvailableUnit;
    /** Booking/record id used as parent_id by resend-form. */
    parentId?: string;
    reservationStatus?: string;
}

/**
 * User data structure
 * Extend this interface as needed for additional user fields
 */
export interface User {
    id: string;
    name?: string | null;
    firstnameEN?: string;
    lastnameEN?: string;
    firstnameAR?: string;
    lastnameAR?: string;
    // Profile fields from /v1/customers/profile-details.
    // Cleared fields stay as `null` (key kept). Emails omitted by ADHA payloads
    // are not written, so a value from /v1/customers/profile can remain.
    emairates_id?: string | null;
    passport_number?: string | null;
    primary_phone?: string | null;
    secondary_phone?: string | null;
    tertiary_phone?: string | null;
    loan_Amount?: number | null;
    primary_country_code?: string | null;
    secondary_country_code?: string | null;
    tertiary_country_code?: string | null;
    primary_email?: string | null;
    secondary_email?: string | null;
    tertiary_email?: string | null;
    nationality?: string | null;
    date_of_birth?: string | null;
    full_name?: string | null;
    registration_status?: string | null;
    customer_number?: string | null;
    customer_id?: string | null;
    documents?: any[] | null;
    /** From profile-details `sales_agent_details` — used by book-appointment APIs */
    sales_agent_id?: string | null;
    sales_agent_name?: string | null;
    sales_agent_phone?: string | null;
    sales_agent_email?: string | null;
    /**
     * From profile-details. Bounds the book-appointment date strip.
     * `null` when the key was missing or empty.
     */
    appointmentBookingStartDate?: string | null;
    appointmentBookingEndDate?: string | null;
    /** From profile-details `bank_details` — Pay Now bank-transfer dialog */
    bank_beneficiary_name?: string | null;
    bank_name?: string | null;
    bank_account_number?: string | null;
    bank_iban?: string | null;
    bank_swift_code?: string | null;
}

export type AuthLoginMethod = 'password' | 'adha' | 'emirates_id';

// Re-export Property type for convenience
export type { Property } from '@/core/api/models/profile';

/**
 * A booked/upcoming appointment for a property visit
 */
export interface BookedAppointment {
    propertyId: string;
    date: string; // display string, e.g. "5 August 2026"
    time: string; // e.g. "12:30 PM"
    badgeLabel: string; // "Today" / "Tomorrow"; empty (no badge) for later dates
    appointmentId?: string; // record id (display/reference only)
    eventId?: string; // Salesforce/calendar event id — required for reschedule/cancel (PATCH /v1/appointments/update)
    unitLabel?: string; // e.g. "A1-401, Al Metlaa" — shown on the Home status card
    image?: string | null; // unit thumbnail URL, when available
}

/**
 * A unit the customer has reserved (Reserve Unit flow), shown as a status
 * card on Home alongside the upcoming appointment card. No date/time — just
 * the unit and its reservation status.
 */
export interface ReservedUnit {
    propertyId: string;
    unitLabel: string;
    image?: string | null;
    badgeLabel: string; // e.g. "Awaiting Signature"
    /** Booking/record id from POST /v1/bookings — used as parent_id by resend-form. */
    parentId?: string;
    /** From GET /v1/bookings/{lead_id} or the POST response, when available. */
    reservationStatus?: string;
    /** Formatted `requested_on` / `created_date` / `created_at` when the API provides one. */
    requestedOn?: string;
}

/**
 * App State Structure
 * Central store for all app-wide reactive data
 */
interface AppState {
    // Authentication state
    auth: {
        token: string | null; // Login token (from login/UAE Pass)
        passcodeToken: string | null; // Passcode token (from verify-passcode)
        isAuthenticated: boolean;
        user: User | null;
        loginResponse: {
            is_passcode_set?: boolean;
            skip_passcode?: boolean;
        } | null; // Temporary storage for login response passcode fields
        /** How the current session was created. Used for APIs like service-requests. */
        loginMethod: AuthLoginMethod | null;
    };

    // Properties array (mapped from units in profile API)
    properties: Property[];

    // ADHA catalogue units (from GET /v1/units with Basic Auth)
    availableUnits: AvailableUnit[];

    // ADHA journey (persisted) — enables ADHA UI + route guards without JWT
    isAdhaJourney: boolean;

    // Login API's `convertedLead` flag (persisted) — when true, profile-details
    // `status` is read instead of `current_status` for reservation status.
    convertedLead: boolean;

    // ADHA loan / application fields from login details
    adhaLoanDetails: AdhaLoanDetails | null;

    // Full GET /v1/customers/profile payload from ADHA login (amenities / project context)
    adhaProjectDetails: UnitDetailsData | null;

    // Unit installments mapped by unit ID
    unitInstallments: UnitInstallmentsMap;

    // Profile loading state
    profileLoading: boolean;

    // Unit installments loading state
    unitInstallmentsLoading: boolean;

    // Global error state for 401/403 errors
    unauthorizedError: {
        message: string | null;
    };

    // UAE Pass registration data (for new users)
    uaePassRegistration: {
        user: any | null; // UAEPassUser from UAE Pass callback
        registrationId: string | null;
        customerId: string | null;
    } | null;

    // Currently booked/upcoming appointment (property visit), if any
    appointment: BookedAppointment | null;

    // Listing replica (latest GET /v1/units for current filter/pages).
    // Reserved unit detail uses pendingUnitReservation.unit (sticky while booking active).
    pendingUnitReservation: PendingUnitReservation | null;
    // Booked unit id from GET /v1/bookings — hide Reserve on other units.
    activeBookedUnitId: string | null;

    // Currently reserved unit (native Reserve Unit flow), if any
    reservedUnit: ReservedUnit | null;

    // Hydration state - tracks if store has been loaded from storage
    _hasHydrated: boolean;

    // Global toast for logout
    logoutToastVisible: boolean;

    // Actions
    setAuth: (
        token: string,
        user: User,
        loginResponse?: { is_passcode_set?: boolean; skip_passcode?: boolean },
        loginMethod?: AuthLoginMethod,
        convertedLead?: boolean,
    ) => void;
    setAdhaSession: (user: User, options?: {
        availableUnits?: AvailableUnit[];
        loanDetails?: AdhaLoanDetails | null;
        projectDetails?: UnitDetailsData | null;
        token?: string | null;
    }) => void;
    setPasscodeToken: (token: string) => void;
    clearAuth: () => Promise<void>;
    clearLoginResponse: () => void;
    setUser: (user: User) => void;
    setProfile: (profileData: Partial<User>) => void;
    setProperties: (properties: Property[]) => void;
    setAvailableUnits: (units: AvailableUnit[]) => void;
    /** Put / refresh a unit at the front of `availableUnits` (reserved booking seed). */
    upsertAvailableUnitFirst: (unit: AvailableUnit) => void;
    setAdhaLoanDetails: (details: AdhaLoanDetails | null) => void;
    setAdhaProjectDetails: (details: UnitDetailsData | null) => void;
    setUnitInstallments: (installments: UnitInstallmentsMap, merge?: boolean) => void;
    setProfileLoading: (loading: boolean) => void;
    setUnitInstallmentsLoading: (loading: boolean) => void;
    setUnauthorizedError: (message: string | null) => void;
    setUaePassRegistration: (data: { user: any; registrationId?: string; customerId?: string } | null) => void;
    clearUaePassRegistration: () => void;
    setAppointment: (appointment: BookedAppointment) => void;
    clearAppointment: () => void;
    setPendingUnitReservation: (reservation: PendingUnitReservation) => void;
    clearPendingUnitReservation: () => void;
    setActiveBookedUnitId: (unitId: string | null) => void;
    clearAdhaWebBookingState: () => void;
    setReservedUnit: (reservedUnit: ReservedUnit) => void;
    clearReservedUnit: () => void;
    setHydrated: (value: boolean) => void;
    setLogoutToastVisible: (visible: boolean) => void;

    // Internal methods for persistence
    _persist?: {
        setOptions: (options: any) => void;
    };
}

// Storage keys for persistence
const APP_STATE_KEY = 'app.state'; // encrypted AsyncStorage
const AUTH_SECRETS_KEY = 'auth.secrets'; // SecureStore (small <2KB)

/** Shallow key/value comparison so a repeated profile sync doesn't re-render consumers. */
function isSameUser(current: User, next: User): boolean {
    const keys = new Set([...Object.keys(current), ...Object.keys(next)]);
    for (const key of keys) {
        if (current[key as keyof User] !== next[key as keyof User]) {
            return false;
        }
    }
    return true;
}

/**
 * Central App Store using Zustand
 * Provides reactive, persistent state management for the entire app
 * 
 * Features:
 * - Automatic persistence to secure storage
 * - Reactive updates across all consumers
 * - Type-safe
 * - Extensible for future data needs
 */
export const useAppStore = create<AppState>((set, get) => ({
    // Initial state
    auth: {
        token: null,
        passcodeToken: null,
        isAuthenticated: false,
        user: null,
        loginResponse: null,
        loginMethod: null,
    },
    properties: [],
    availableUnits: [],
    isAdhaJourney: false,
    convertedLead: false,
    adhaLoanDetails: null,
    adhaProjectDetails: null,
    unitInstallments: {},
    profileLoading: false,
    unitInstallmentsLoading: false,
    unauthorizedError: {
        message: null,
    },
    uaePassRegistration: null,
    appointment: null,
    pendingUnitReservation: null,
    activeBookedUnitId: null,
    reservedUnit: null,
    _hasHydrated: false,
    logoutToastVisible: false,

    // Set authentication state (email / normal login) — clears ADHA journey
    setAuth: (
        token: string,
        user: User,
        loginResponse?: { is_passcode_set?: boolean; skip_passcode?: boolean },
        loginMethod: AuthLoginMethod = 'password',
        convertedLead: boolean = false,
    ) => {
        logger.info('AppStore:Setting auth', { userId: user.id, primaryEmail: user.primary_email, loginMethod, convertedLead });
        set({
            auth: {
                token,
                passcodeToken: null, // Clear passcode token on new login
                isAuthenticated: true,
                user,
                loginResponse: loginResponse || null,
                loginMethod,
            },
            isAdhaJourney: false,
            convertedLead,
            availableUnits: [],
            adhaLoanDetails: null,
            adhaProjectDetails: null,
            pendingUnitReservation: null,
            activeBookedUnitId: null,
            reservedUnit: null,
        });
        logger.info('AppStore:Auth state updated', { isAuthenticated: true, isAdhaJourney: false, convertedLead });
    },

    // ADHA journey session — Basic Auth APIs by default; pass `token` when the
    // ADHA UAE Pass login call returned a JWT (used as Bearer for non-ADHA endpoints).
    setAdhaSession: (user: User, options?: {
        availableUnits?: AvailableUnit[];
        loanDetails?: AdhaLoanDetails | null;
        projectDetails?: UnitDetailsData | null;
        token?: string | null;
    }) => {
        logger.info('AppStore:Setting ADHA session', { userId: user.id });
        set({
            auth: {
                token: options?.token ?? null,
                passcodeToken: null,
                isAuthenticated: true,
                user,
                loginResponse: null,
                loginMethod: 'adha',
            },
            isAdhaJourney: true,
            convertedLead: false,
            availableUnits: options?.availableUnits ?? get().availableUnits,
            adhaLoanDetails: options?.loanDetails !== undefined ? options.loanDetails : get().adhaLoanDetails,
            adhaProjectDetails: options?.projectDetails !== undefined ? options.projectDetails : get().adhaProjectDetails,
            properties: [],
            unitInstallments: {},
            pendingUnitReservation: null,
            activeBookedUnitId: null,
            reservedUnit: null,
        });
        logger.info('AppStore:ADHA session set', { isAdhaJourney: true });
    },

    // Clear login response (after password change flow)
    clearLoginResponse: () => {
        set((state) => ({
            auth: {
                ...state.auth,
                loginResponse: null,
            },
        }));
        logger.info('AppStore:Login response cleared');
    },

    // Set passcode token (from verify-passcode)
    setPasscodeToken: (token: string) => {
        logger.info('AppStore:Setting passcode token');
        const currentAuth = get().auth;
        set({
            auth: {
                ...currentAuth,
                passcodeToken: token,
                isAuthenticated: true, // User is authenticated via passcode
            },
        });
        logger.info('AppStore:Passcode token set');
    },

    // Clear authentication state (logout)
    clearAuth: async () => {
        logger.info('AppStore:Clearing auth - clearing all data including passcode');

        try {
            await clearDynatraceUser();
        } catch (error) {
            logger.warn('AppStore:Failed to clear Dynatrace user tag', { error });
        }

        // Clear everything - user data, tokens, passcode, properties, installments, ADHA
        set({
            auth: {
                token: null,
                passcodeToken: null,
                isAuthenticated: false,
                user: null,
                loginResponse: null,
                loginMethod: null,
            },
            properties: [],
            availableUnits: [],
            isAdhaJourney: false,
            convertedLead: false,
            adhaLoanDetails: null,
            adhaProjectDetails: null,
            unitInstallments: {},
            profileLoading: false,
            unitInstallmentsLoading: false,
            appointment: null,
            pendingUnitReservation: null,
            activeBookedUnitId: null,
            reservedUnit: null,
        });

        // Use logout service for comprehensive cleanup (non-blocking, graceful)
        // This will clear all data including passcode
        void import('@/core/services/logoutService')
            .then(({ logoutService }) => logoutService.quickLogout())
            .catch((error) => {
                logger.error('AppStore:Failed to clear local data via logout service', error);
            });
        logger.info('AppStore:Auth cleared - all data including passcode cleared');
    },

    // Update user data
    setUser: (user: User) => {
        logger.info('AppStore:Updating user data', { userId: user.id });
        const currentAuth = get().auth;
        set({
            auth: {
                ...currentAuth,
                user,
            },
        });
        logger.debug('AppStore:User data updated');
    },

    // Merge profile-details fields into user. Login-owned keys outside the patch stay.
    setProfile: (profileData: Partial<User>) => {
        const currentAuth = get().auth;
        if (!currentAuth.user) {
            logger.warn('AppStore:Cannot update profile - no user in store');
            return;
        }

        const mergedUser: User = { ...currentAuth.user, ...profileData };
        if (isSameUser(currentAuth.user, mergedUser)) {
            return;
        }

        logger.info('AppStore:Updating profile data');
        set({
            auth: {
                ...currentAuth,
                user: mergedUser,
            },
        });
    },

    // Set properties array
    setProperties: (properties: Property[]) => {
        logger.info('AppStore:Setting properties', { count: properties.length });
        set({ properties });
        logger.debug('AppStore:Properties updated');
    },

    setAvailableUnits: (units: AvailableUnit[]) => {
        logger.info('AppStore:Setting available units', { count: units.length });
        const pending = get().pendingUnitReservation;
        const bookedId = get().activeBookedUnitId || pending?.unitId || null;

        // Keep reserved unit as first entry even when listing pages/filters omit it.
        if (bookedId && pending?.unit) {
            const fromApi = units.find((unit) => unit.unit_id === bookedId);
            const rest = units.filter((unit) => unit.unit_id !== bookedId);
            const reservationStatus =
                pending.reservationStatus ||
                fromApi?.reservation_status ||
                pending.unit.reservation_status;
            const mergedUnit: AvailableUnit = fromApi
                ? { ...pending.unit, ...fromApi }
                : { ...pending.unit };
            const first: AvailableUnit = reservationStatus
                ? { ...mergedUnit, reservation_status: reservationStatus }
                : mergedUnit;
            const next = [first, ...rest];
            const prev = get().availableUnits;
            if (
                prev.length === next.length &&
                prev.every((unit, index) => unit.unit_id === next[index]?.unit_id) &&
                prev[0]?.reservation_status === first.reservation_status &&
                prev[0]?.total_price === first.total_price
            ) {
                return;
            }
            set({
                availableUnits: next,
                pendingUnitReservation: {
                    ...pending,
                    unit: first,
                },
            });
            return;
        }

        const prev = get().availableUnits;
        if (
            prev.length === units.length &&
            prev.every((unit, index) => unit.unit_id === units[index]?.unit_id)
        ) {
            return;
        }
        set({ availableUnits: units });
    },

    upsertAvailableUnitFirst: (unit: AvailableUnit) => {
        const current = get().availableUnits;
        const existing = current.find((row) => row.unit_id === unit.unit_id);
        const merged: AvailableUnit = existing ? { ...existing, ...unit } : unit;
        const alreadyFirst =
            current[0]?.unit_id === merged.unit_id &&
            current[0]?.unit_number === merged.unit_number &&
            current[0]?.total_price === merged.total_price &&
            current[0]?.total_area === merged.total_area &&
            current[0]?.reservation_status === merged.reservation_status &&
            current[0]?.handover_date === merged.handover_date;
        if (alreadyFirst) {
            return;
        }
        const rest = current.filter((row) => row.unit_id !== unit.unit_id);
        logger.info('AppStore:Upsert available unit first', {
            unitId: unit.unit_id,
        });
        set({ availableUnits: [merged, ...rest] });
    },

    setAdhaLoanDetails: (details: AdhaLoanDetails | null) => {
        set({ adhaLoanDetails: details });
    },

    setAdhaProjectDetails: (details: UnitDetailsData | null) => {
        set({ adhaProjectDetails: details });
    },

    // Set profile loading state
    setProfileLoading: (loading: boolean) => {
        set({ profileLoading: loading });
    },

    // Set unit installments (supports both full replacement and progressive updates)
    setUnitInstallments: (installments: UnitInstallmentsMap, merge: boolean = false) => {
        if (merge) {
            // Progressive update: merge with existing installments
            const current = get().unitInstallments;
            const merged = { ...current, ...installments };
            logger.info('AppStore:Merging unit installments', { 
                newCount: Object.keys(installments).length,
                totalCount: Object.keys(merged).length 
            });
            set({ unitInstallments: merged });
        } else {
            // Full replacement (default behavior for backward compatibility)
            logger.info('AppStore:Setting unit installments', { count: Object.keys(installments).length });
            set({ unitInstallments: installments });
        }
        logger.debug('AppStore:Unit installments updated');
    },

    // Set unit installments loading state
    setUnitInstallmentsLoading: (loading: boolean) => {
        set({ unitInstallmentsLoading: loading });
    },

    // Set unauthorized error message (for 401/403 dialogs)
    setUnauthorizedError: (message: string | null) => {
        set({
            unauthorizedError: {
                message,
            },
        });
    },

    // Set UAE Pass registration data
    setUaePassRegistration: (data: { user: any; registrationId?: string; customerId?: string } | null) => {
        if (data === null) {
            set({ uaePassRegistration: null });
            logger.info('AppStore:Cleared UAE Pass registration data');
        } else {
            set({
                uaePassRegistration: {
                    user: data.user,
                    registrationId: data.registrationId || null,
                    customerId: data.customerId || null,
                },
            });
            logger.info('AppStore:Set UAE Pass registration data', {
                hasUser: !!data.user,
                registrationId: data.registrationId,
                customerId: data.customerId,
            });
        }
    },

    // Set the current booked/upcoming appointment
    setAppointment: (appointment: BookedAppointment) => {
        logger.info('AppStore:Setting appointment', { propertyId: appointment.propertyId });
        set({ appointment });
    },

    // Clear the current appointment (e.g. on cancel)
    clearAppointment: () => {
        logger.info('AppStore:Clearing appointment');
        set({ appointment: null });
    },

    setPendingUnitReservation: (reservation: PendingUnitReservation) => {
        const current = get().pendingUnitReservation;
        if (
            current?.unitId === reservation.unitId &&
            current?.reservationStatus === reservation.reservationStatus &&
            current?.unitTitle === reservation.unitTitle &&
            current?.parentId === reservation.parentId &&
            current?.requestedOn === reservation.requestedOn &&
            get().activeBookedUnitId === reservation.unitId
        ) {
            return;
        }
        logger.info('AppStore:Setting pending unit reservation', {
            unitId: reservation.unitId,
        });
        set({
            pendingUnitReservation: reservation,
            activeBookedUnitId: reservation.unitId,
        });
    },

    clearPendingUnitReservation: () => {
        logger.info('AppStore:Clearing pending unit reservation');
        set({ pendingUnitReservation: null, activeBookedUnitId: null });
    },

    setActiveBookedUnitId: (unitId: string | null) => {
        set({ activeBookedUnitId: unitId });
    },

    clearAdhaWebBookingState: () => {
        // Keep the catalogue row so details (opened via View Reservation while
        // the unit is outside the price filter) still resolves after cancel.
        // Replace the row (don't merge) so reservation_status is dropped.
        const pending = get().pendingUnitReservation;
        if (pending?.unit?.unit_id) {
            const { reservation_status: _status, ...catalogueRow } = pending.unit;
            const rest = get().availableUnits.filter(
                (row) => row.unit_id !== pending.unitId,
            );
            set({
                availableUnits: [catalogueRow, ...rest],
                pendingUnitReservation: null,
                activeBookedUnitId: null,
            });
            return;
        }
        get().clearPendingUnitReservation();
    },

    // Set the current reserved unit (native Reserve Unit flow)
    setReservedUnit: (reservedUnit: ReservedUnit) => {
        logger.info('AppStore:Setting reservedUnit', { propertyId: reservedUnit.propertyId });
        set({ reservedUnit });
    },

    // Clear the current reserved unit
    clearReservedUnit: () => {
        logger.info('AppStore:Clearing reservedUnit');
        set({ reservedUnit: null });
    },

    // Clear UAE Pass registration data
    clearUaePassRegistration: () => {
        set({ uaePassRegistration: null });
        logger.info('AppStore:Cleared UAE Pass registration data');
        // Also clear registration IDs from AsyncStorage
        void (async () => {
            try {
                const { RegistrationStorage } = await import('@/modules/onboarding/registration/storage/registrationStorage');
                await RegistrationStorage.clearRegistrationIds();
            } catch (error) {
                logger.error('AppStore:Failed to clear registration IDs', error);
            }
        })();
    },

    // Set hydration status
    setHydrated: (value: boolean) => {
        logger.debug('AppStore:Hydration status', { hydrated: value });
        set({ _hasHydrated: value });
    },

    setLogoutToastVisible: (visible: boolean) => {
        set({ logoutToastVisible: visible });
    },
}));

// Configure persistence using Zustand's built-in middleware
// Note: Zustand doesn't have built-in persist, so we'll handle it manually
// But we can use a pattern similar to what zustand-persist does

/**
 * Initialize store from persisted storage
 * This should be called once at app startup before any navigation
 */
export const initializeAppStore = async (): Promise<void> => {
    try {
        logger.info('AppStore:Initializing from storage');
        await clearStaleKeychainOnFreshInstall();
        const [appState, authSecretsRaw] = await Promise.all([
            encryptedStorage.getDecrypted<Partial<AppState>>(APP_STATE_KEY),
            secureStorage.getItem(AUTH_SECRETS_KEY),
        ]);

        const authSecrets = authSecretsRaw ? (JSON.parse(authSecretsRaw) as { token: string | null; passcodeToken?: string | null; userId: string | null }) : { token: null, passcodeToken: null, userId: null };

        const isAdhaJourney = !!appState?.isAdhaJourney;
        const persistedUser = (appState?.auth as any)?.user ?? null;
        const hasTokenAuth = !!authSecrets.token || !!authSecrets.passcodeToken;
        // Leftover iOS Keychain JWT without a user is not a session — show sign-in.
        const isAuthenticated = isAdhaJourney || (hasTokenAuth && !!persistedUser);

        logger.info('AppStore:Loaded persisted state', {
            hasAuth: hasTokenAuth,
            isAdhaJourney,
            hasUser: !!persistedUser,
            isAuthenticated,
        });

        useAppStore.setState({
            auth: {
                token: authSecrets.token,
                passcodeToken: authSecrets.passcodeToken || null,
                isAuthenticated,
                user: persistedUser,
                loginResponse: null, // Don't persist login response
                loginMethod:
                    (appState?.auth as { loginMethod?: AuthLoginMethod | null } | undefined)?.loginMethod
                    ?? (isAdhaJourney ? 'adha' : null),
            },
            properties: appState?.properties || [],
            availableUnits: appState?.availableUnits || [],
            isAdhaJourney,
            convertedLead: !!appState?.convertedLead,
            adhaLoanDetails: appState?.adhaLoanDetails || null,
            adhaProjectDetails: appState?.adhaProjectDetails || null,
            unitInstallments: appState?.unitInstallments || {},
            uaePassRegistration: appState?.uaePassRegistration || null,
            appointment: appState?.appointment || null,
            pendingUnitReservation: appState?.pendingUnitReservation || null,
            activeBookedUnitId:
                appState?.activeBookedUnitId ||
                appState?.pendingUnitReservation?.unitId ||
                null,
            reservedUnit: appState?.reservedUnit || null,
            profileLoading: false,
            unitInstallmentsLoading: false,
            _hasHydrated: true,
        });
    } catch (error) {
        logger.error('AppStore:Failed to initialize from storage', error);
        useAppStore.setState({ _hasHydrated: true });
    }
};

async function persistAppState(state: AppState): Promise<void> {
    try {
        // Persist secrets (small): token + passcodeToken + userId only
        const secrets = JSON.stringify({
            token: state.auth.token,
            passcodeToken: state.auth.passcodeToken,
            userId: state.auth.user?.id ?? null
        });
        await secureStorage.setItem(AUTH_SECRETS_KEY, secrets);

        // Persist app slice (encrypted): user, properties, ADHA catalogue, journey flag, installments
        const toPersist = {
            auth: {
                user: state.auth.user ?? null,
                loginMethod: state.auth.loginMethod ?? null,
            },
            properties: state.properties,
            availableUnits: state.availableUnits,
            isAdhaJourney: state.isAdhaJourney,
            convertedLead: state.convertedLead,
            adhaLoanDetails: state.adhaLoanDetails,
            adhaProjectDetails: state.adhaProjectDetails,
            unitInstallments: state.unitInstallments,
            uaePassRegistration: state.uaePassRegistration,
            appointment: state.appointment,
            pendingUnitReservation: state.pendingUnitReservation,
            activeBookedUnitId: state.activeBookedUnitId,
            reservedUnit: state.reservedUnit,
        } as Partial<AppState>;
        await encryptedStorage.setEncrypted(APP_STATE_KEY, toPersist);
    } catch (error) {
        logger.error('AppStore:Failed to persist state', error);
    }
}

/**
 * Persist store changes to secure storage
 * This subscription watches for state changes and persists them
 *
 * Debounced (400ms trailing): screens like HomeScreenMobileAdhaMobile /
 * PropertyDetailsScreenAdhaMobile fire several `set()` calls back-to-back
 * (booking sync, profile overlay, reserve/cancel) — each one used to run its
 * own full JSON.stringify + AES-GCM encrypt + AsyncStorage write of the
 * *entire* persisted slice synchronously on the JS thread. Debouncing
 * collapses a burst of writes into a single encrypt+write using the latest
 * state once things settle, instead of one per intermediate `set()`.
 */
let isSubscribed = false;
let persistDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const PERSIST_DEBOUNCE_MS = 400;

/** Web: wait this long after the last persisted change, then write when the tab is idle. */
const WEB_PERSIST_DEBOUNCE_MS = 1000;
const WEB_PERSIST_IDLE_TIMEOUT_MS = 3000;

/**
 * Fields written by `persistAppState`. On web the full write (JSON.stringify of
 * the whole unit catalogue + encrypt) blocks the main thread for seconds once
 * thousands of units are loaded, so it only runs when one of these changed.
 */
function persistedSlice(state: AppState): readonly unknown[] {
    return [
        state.auth.token,
        state.auth.passcodeToken,
        state.auth.user,
        state.auth.loginMethod,
        state.properties,
        state.availableUnits,
        state.isAdhaJourney,
        state.convertedLead,
        state.adhaLoanDetails,
        state.adhaProjectDetails,
        state.unitInstallments,
        state.uaePassRegistration,
        state.appointment,
        state.pendingUnitReservation,
        state.activeBookedUnitId,
        state.reservedUnit,
    ];
}

function sameSlice(left: readonly unknown[], right: readonly unknown[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}

function subscribeWebPersistence(): void {
    let lastSlice = persistedSlice(useAppStore.getState());
    let timer: ReturnType<typeof setTimeout> | null = null;
    let idleHandle: number | null = null;
    const idleWindow = globalThis as typeof globalThis & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (handle: number) => void;
    };

    const cancelPending = () => {
        if (timer != null) {
            clearTimeout(timer);
            timer = null;
        }
        if (idleHandle != null && idleWindow.cancelIdleCallback) {
            idleWindow.cancelIdleCallback(idleHandle);
        }
        idleHandle = null;
    };

    const writeNow = () => {
        cancelPending();
        void persistAppState(useAppStore.getState());
    };

    const schedule = () => {
        cancelPending();
        timer = setTimeout(() => {
            timer = null;
            if (idleWindow.requestIdleCallback) {
                idleHandle = idleWindow.requestIdleCallback(
                    () => {
                        idleHandle = null;
                        void persistAppState(useAppStore.getState());
                    },
                    { timeout: WEB_PERSIST_IDLE_TIMEOUT_MS },
                );
                return;
            }
            void persistAppState(useAppStore.getState());
        }, WEB_PERSIST_DEBOUNCE_MS);
    };

    useAppStore.subscribe((state) => {
        const nextSlice = persistedSlice(state);
        if (sameSlice(lastSlice, nextSlice)) {
            return;
        }
        // Login / logout must reach storage before a reload, so token changes write at once.
        const authChanged =
            nextSlice[0] !== lastSlice[0] || nextSlice[1] !== lastSlice[1];
        lastSlice = nextSlice;
        if (authChanged) {
            writeNow();
            return;
        }
        schedule();
    });

    if (typeof document !== 'undefined') {
        const flushIfPending = () => {
            if (timer != null || idleHandle != null) {
                writeNow();
            }
        };
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                flushIfPending();
            }
        });
        globalThis.addEventListener?.('pagehide', flushIfPending);
    }
}

/** Flush a pending debounced write immediately (no-op if nothing is pending). */
function flushPendingPersist(): void {
    if (!persistDebounceTimer) {
        return;
    }
    clearTimeout(persistDebounceTimer);
    persistDebounceTimer = null;
    void persistAppState(useAppStore.getState());
}

export const subscribeToAppStorePersistence = (): void => {
    if (isSubscribed) return;

    isSubscribed = true;

    // Mobile-only: the debounce + flush-on-background below exists to fix an
    // iOS-specific freeze (console logging + full-state encrypt cost stacking
    // up under React Native's bridge). Web never had that problem, and
    // `AppState`'s background/inactive semantics don't map cleanly onto a
    // browser tab anyway — so web keeps the exact original behavior
    // (persist immediately on every state change) untouched.
    if (Platform.OS === 'web') {
        subscribeWebPersistence();
        logger.info('AppStore:Persistence subscription active (web, idle)');
        return;
    }

    useAppStore.subscribe(() => {
        if (persistDebounceTimer) {
            clearTimeout(persistDebounceTimer);
        }
        persistDebounceTimer = setTimeout(() => {
            persistDebounceTimer = null;
            // Read the latest state at fire time, not whatever triggered this tick.
            void persistAppState(useAppStore.getState());
        }, PERSIST_DEBOUNCE_MS);
    });

    // Closes the one gap the debounce above opens: if the OS suspends/kills
    // the app within the debounce window, the last change could be lost.
    // Backgrounding always fires before a suspend/kill, so flushing here
    // guarantees the same "always persisted" behavior the un-debounced
    // version had, without giving up the batching benefit while foregrounded.
    RNAppState.addEventListener('change', (nextState) => {
        if (nextState === 'background' || nextState === 'inactive') {
            flushPendingPersist();
        }
    });

    logger.info('AppStore:Persistence subscription active (mobile, debounced)');
};

